// client/src/world/flora/library.ts
/**
 * Lazy per-planet geometry cache keyed (species, season, maturityBucket):
 * 4 variants per key, built on first request. A tile's season/maturity is
 * locally constant, so per-tile distinct-geometry count (and draw calls)
 * matches the old eager 4-variants-per-kind system.
 */
import * as THREE from "three";
import { rngFromString, type PlanetDef, type Rng } from "@hyperlight/shared";
import { buildTree, buildTreeFar } from "./grammar";
import { SPECIES } from "./species";
import { BUCKET_MATURITY } from "./field";
import type { C3, Season, SpeciesId, SpeciesParams } from "./types";
import { landscapeColors } from "../landscapePalette";
import { hasLivingGround } from "../groundSurface";

export class FloraLibrary {
  private cache = new Map<string, THREE.BufferGeometry[]>();
  private speciesCache = new Map<SpeciesId, SpeciesParams>();
  constructor(private def: PlanetDef) {}

  private species(id: SpeciesId): SpeciesParams {
    const source = SPECIES[id];
    if (!hasLivingGround(this.def) || id === "sakura") return source;
    let sp = this.speciesCache.get(id);
    if (!sp) {
      const pigment = landscapeColors(this.def).foliage;
      // Keep each species' lightness and silhouette identity; bring its summer
      // pigment into the same family as the understory. Seasons stay distinct.
      const leafSummer = source.palette.leafSummer.map((c, i) => c * .75 + pigment[i] * .25) as C3;
      sp = { ...source, palette: { ...source.palette, leafSummer } };
      this.speciesCache.set(id, sp);
    }
    return sp;
  }

  variants(species: SpeciesId, season: Season, bucket: 0 | 1 | 2): THREE.BufferGeometry[] {
    const key = `${species}:${season}:${bucket}`;
    let v = this.cache.get(key);
    if (!v) {
      v = [];
      for (let i = 0; i < 4; i++) {
        const shapeKey = botanicalKey(species, bucket) ?? key;
        const rng = rngFromString(`${this.def.seed}:flora2:${shapeKey}:${i}`);
        v.push(buildTree(rng, this.species(species), { season, maturity: BUCKET_MATURITY[bucket] }));
      }
      this.cache.set(key, v);
    }
    return v;
  }

  /** Far silhouettes: ≤150 tris; roots ≤320, palms/acacias ≤450, cypress ≤850. */
  variantsFar(species: SpeciesId, season: Season, bucket: 0 | 1 | 2): THREE.BufferGeometry[] {
    const key = `far:${species}:${season}:${bucket}`;
    let v = this.cache.get(key);
    if (!v) {
      v = [];
      for (let i = 0; i < 4; i++) {
        // Leaf and evergreen LODs/seasons share a botanical layout. Willow
        // retains its existing generator and seeds.
        const shapeKey = botanicalKey(species, bucket) ?? key;
        const rng = rngFromString(`${this.def.seed}:flora2:${shapeKey}:${i}`);
        v.push(buildTreeFar(rng, this.species(species), { season, maturity: BUCKET_MATURITY[bucket] }));
      }
      this.cache.set(key, v);
    }
    return v;
  }

  dispose(): void {
    for (const set of this.cache.values()) for (const g of set) g.dispose();
    this.cache.clear();
    this.speciesCache.clear();
  }
}

function botanicalKey(species: SpeciesId, bucket: number): string | undefined {
  const style = SPECIES[species].canopy.style;
  if (style === "cypress") return `cypress1:${species}:${bucket}`;
  if (style === "umbrella") return `acacia1:${species}:${bucket}`;
  if (style === "pinnate") return `palm1:${species}:${bucket}`;
  if (style === "blobs") return `broadleaf1:${species}:${bucket}`;
  if (style === "tiers" || style === "droopTiers") return `conifer1:${species}:${bucket}`;
  return undefined;
}

/** Climate bands → species stands: primary from the band's head, secondary distinct. */
const MIX: Array<{ max: number; list: SpeciesId[] }> = [
  { max: -0.2, list: ["spruce", "pine", "birch"] },
  { max: 0.15, list: ["pine", "birch", "forest", "spruce"] },
  { max: 0.5, list: ["oak", "forest", "birch", "sakura"] },
  { max: Infinity, list: ["willow", "sakura", "oak", "forest"] },
];

export function forestMix(temp: number, moist: number, rng: Rng): { primary: SpeciesId; secondary: SpeciesId } {
  const band = MIX.find((b) => temp < b.max)!;
  const primaryRoll = rng(), secondaryRoll = rng();
  const ordinary: SpeciesId[] = band.list.filter(id => id !== "sakura");
  const primary = ordinary[Math.floor(primaryRoll * 2)];
  let secondary = ordinary[Math.floor(secondaryRoll * ordinary.length)];
  if (secondary === primary) secondary = ordinary[(ordinary.indexOf(secondary) + 1) % ordinary.length];
  // A blossom stand is a rare accent: 3% of eligible warm stand selections,
  // carried only by the secondary cohort (20% in forest/far bands). Reuse the
  // existing draw so downstream transforms and unrelated props keep their RNG.
  if (temp >= .15 && moist > -.1 && moist < .45 && secondaryRoll > .97) secondary = "sakura";
  if (moist > 0.45 && temp > 0.2 && rng() < 0.5 && primary !== "willow") secondary = "willow";
  return { primary, secondary };
}
